import { useState } from 'owa-react';
import React from 'react';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import {
    Button,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Tooltip,
} from '@fluentui/react-components';
import type { OpenPopoverEvents, OnOpenChangeData } from '@fluentui/react-components';
import { ArrowCurveDownLeftRegular } from '@fluentui/react-icons';
import getMasterCategoryList from 'owa-categories/lib/utils/getMasterCategoryList';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import {
    getStore as getListViewStore,
    type SortColumn,
    getListViewState,
    onAutoOpenJumpToMutator,
    setIsFocusWithinJumpToMutator,
} from 'owa-mail-list-store';
import type { MailFolderTableQuery } from 'owa-mail-list-store';
import { resetFocus } from 'owa-mail-focus-manager';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { jumpToButtonLabel } from './MailListJumpToPopover.locstring.json';
import {
    jumpToButton,
    jumpToButtonWithLabel,
    jumpToButtonOpened,
    jumpToIcon,
    popoverSurface,
    full,
    medium,
    compact,
    bleedThroughBackground,
} from './MailListJumpToPopover.scss';
import JumpToCalloutContent from './JumpToCalloutContent';

import classnames from 'owa-classnames';

export interface MailListJumpToPopoverProps {
    tableViewId: string;
    showButtonLabel: boolean;
    isBleedThrough?: boolean;
}

const defaultExport: React.FunctionComponent<MailListJumpToPopoverProps> = observer(
    function MailListJumpToPopover(props: MailListJumpToPopoverProps) {
        const { showButtonLabel, tableViewId, isBleedThrough } = props;
        const tableView = getListViewStore().tableViews.get(tableViewId);
        const mailboxInfo = getMailboxInfo(tableView);
        const sortColumn = (tableView?.tableQuery as MailFolderTableQuery)?.sortBy?.sortColumn;
        const categoryList = getMasterCategoryList(mailboxInfo);
        const densityMode = getDensityModeCssClass(full, medium, compact);
        const listViewState = getListViewState();
        const [open, setOpen] = useState(false);

        React.useEffect(() => {
            // When the user switches to sort type From or Subject, we want to auto-open the jump to popover and place focus in it.
            // We only want to do this once the table has loaded.
            if (tableView?.isInitialLoadComplete) {
                // This setTimeout is needed because the ML calls resetFocus based on the same tableView prop so we need to
                // time this to be after so this gets focus.
                setTimeout(() => {
                    if (listViewState.shouldAutoOpenJumpTo) {
                        setOpen(true);
                        onAutoOpenJumpToMutator();
                    }
                }, 10);
            }
        }, [listViewState.shouldAutoOpenJumpTo, tableView?.isInitialLoadComplete]);

        const handleOpenChange = React.useCallback(
            (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
                setOpen(data.open || false);
                setIsFocusWithinJumpToMutator(data.open);
            },
            []
        );

        const onKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Escape') {
                setOpen(false);
                resetFocus('JumpToCalloutDismissed');
            }
        }, []);

        const onClose = React.useCallback(() => {
            setOpen(false);
            setIsFocusWithinJumpToMutator(false);
            resetFocus('JumpToCalloutDismissed');
        }, []);

        // If the jump to button is being used to display the label, then we don't want
        // to wrap it in a Tooltip. Otherwise, we do so that we can display the label
        // when the user hovers over the button.
        const menuButton = React.useMemo(() => {
            const jumpToButtonIcon = (
                <ArrowCurveDownLeftRegular className={classnames(jumpToIcon, densityMode)} />
            );

            if (showButtonLabel) {
                return (
                    <Button
                        icon={jumpToButtonIcon}
                        className={classnames(
                            jumpToButtonWithLabel,
                            open && jumpToButtonOpened,
                            densityMode,
                            isBleedThrough && bleedThroughBackground
                        )}
                    >
                        {loc(jumpToButtonLabel)}
                    </Button>
                );
            } else {
                return (
                    <Tooltip content={loc(jumpToButtonLabel)} relationship="label">
                        <Button
                            icon={jumpToButtonIcon}
                            className={classnames(
                                jumpToButton,
                                open && jumpToButtonOpened,
                                densityMode,
                                isBleedThrough && bleedThroughBackground
                            )}
                        />
                    </Tooltip>
                );
            }
        }, [showButtonLabel, open, densityMode]);

        if (!tableView || (sortColumn === 14 && categoryList.length === 0)) {
            return null;
        }

        return (
            <Popover
                trapFocus={true}
                positioning={'below-start'}
                open={open}
                onOpenChange={handleOpenChange}
                closeOnScroll={true}
            >
                <PopoverTrigger disableButtonEnhancement={true}>{menuButton}</PopoverTrigger>
                <PopoverSurface className={popoverSurface} role={'group'} aria-modal={undefined}>
                    <JumpToCalloutContent
                        mailboxInfo={mailboxInfo}
                        tableView={tableView}
                        onClose={onClose}
                        onKeyDownCallback={onKeyDown}
                    />
                </PopoverSurface>
            </Popover>
        );
    },
    'MailListJumpToPopover'
);
export default defaultExport;
